import { type MouseEvent, useEffect, useState } from 'react';
import type { Lot } from '../data/mockLots';
import { useCountdown } from '../hooks/useCountdown';
import { RingTimer } from './RingTimer';
import { SkinFrame } from './SkinFrame';
import { BidderStack } from './BidderPill';

const RARITY_LABEL: Record<string, { label: string; color: string }> = {
  consumer: { label: 'CONSUMER', color: '#b0c3d9' },
  industrial: { label: 'INDUSTRIAL', color: '#5e98d9' },
  milspec: { label: 'MIL-SPEC', color: '#4b69ff' },
  restricted: { label: 'RESTRICTED', color: '#8847ff' },
  classified: { label: 'CLASSIFIED', color: '#d32ce6' },
  covert: { label: 'COVERT', color: '#eb4b4b' },
  contraband: { label: 'CONTRABAND', color: '#e4ae39' },
};

const WEAR_LABEL: Record<string, string> = {
  FN: 'Factory New',
  MW: 'Minimal Wear',
  FT: 'Field-Tested',
  WW: 'Well-Worn',
  BS: 'Battle-Scarred',
};

export function HeroLot({ lot, onTap, onBid }: { lot: Lot; onTap?: () => void; onBid?: () => void }) {
  const secondsLeft = useCountdown(lot.timerEndsAt);
  const [flash, setFlash] = useState(0);

  useEffect(() => {
    setFlash((f) => f + 1);
  }, [lot.bidsCount]);

  const rarity = RARITY_LABEL[lot.rarity] ?? { label: lot.rarity.toUpperCase(), color: '#7a8595' };
  const wearLabel = WEAR_LABEL[lot.wear] ?? lot.wear;
  const discountPct = Math.round((1 - lot.currentPrice / lot.marketPrice) * 100);

  const handleBid = (e: MouseEvent) => {
    e.stopPropagation();
    onBid?.();
  };

  return (
    <section
      className="relative anim-rise hud-corners"
      style={{ animationDelay: '60ms' }}
    >
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <SkinFrame weapon={lot.weapon} palette={lot.palette} size="lg" />
        </div>

        <div className="absolute inset-0 scanlines mix-blend-overlay opacity-60 pointer-events-none" />

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,14,20,0.55) 0%, rgba(10,14,20,0.15) 40%, rgba(10,14,20,0.92) 100%)',
          }}
        />

        <div className="relative z-10 flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.3em] text-danger anim-blink flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-danger" />
              LIVE
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] text-fg-muted">
              · {rarity.label}
            </span>
          </div>

          <RingTimer
            secondsLeft={secondsLeft}
            size={88}
            stroke={5}
            scaleSec={60}
            flashTrigger={flash}
            showLabel={false}
          />
        </div>

        <div className="relative z-10 px-4 pt-16 pb-4">
          <h2 className="font-display font-bold text-[34px] leading-[0.95] tracking-tight text-fg">
            {lot.weaponLabel}
          </h2>
          <h3
            className="font-display font-medium text-[22px] leading-tight mt-0.5"
            style={{ color: lot.palette.accent ?? lot.palette.to }}
          >
            | {lot.skinName}
          </h3>
          <p className="font-mono text-[10px] tracking-[0.18em] text-fg-muted mt-2">
            {lot.wear} · {wearLabel} · float {lot.float.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="relative bg-bg-elev/95 border-t border-edge px-4 py-3">
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col">
            <span className="font-mono text-[9px] tracking-[0.3em] text-fg-muted">CURRENT BID</span>
            <div className="flex items-baseline gap-1.5">
              <span
                className="font-mono font-bold text-[32px] tabular-nums leading-none"
                style={{ color: '#00ff94', textShadow: '0 0 14px rgba(0,255,148,0.35)' }}
              >
                ${lot.currentPrice.toFixed(2)}
              </span>
              <span className="font-mono text-[11px] text-accent">+$0.01</span>
            </div>
            <span className="font-mono text-[10px] tracking-wider text-fg-muted mt-1">
              MARKET ${lot.marketPrice.toFixed(2)}{' '}
              <span className="text-success ml-1">−{discountPct}%</span>
            </span>
          </div>

          <div className="flex flex-col items-end gap-1.5">
            <BidderStack bidders={lot.recentBidders} max={3} />
            <span className="font-mono text-[10px] tracking-wider text-fg-muted">
              {lot.bidsCount} BIDS · {lot.totalBidders} PLAYERS
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
          <button
            type="button"
            onClick={handleBid}
            className="relative group overflow-hidden bg-accent text-bg py-3.5 font-display font-bold tracking-wider text-sm uppercase active:scale-[0.98] transition-transform"
            style={{ boxShadow: '0 0 24px rgba(255, 61, 167, 0.4), inset 0 0 0 1px rgba(255,255,255,0.12)' }}
          >
            <span className="absolute inset-0 anim-sweep bg-gradient-to-r from-transparent via-white/30 to-transparent" style={{ width: '40%' }} />
            <span className="relative flex items-center justify-center gap-2">
              <span>BID</span>
              <span className="font-mono">+$0.10</span>
              <span className="font-mono text-[10px] opacity-80">→ +10s</span>
            </span>
          </button>

          <button
            type="button"
            onClick={onTap}
            className="flex items-center justify-center gap-1.5 px-4 border border-primary/40 bg-primary/5 text-primary font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-primary/10 hover:border-primary/70 active:scale-[0.99] transition-all"
          >
            <span>Open</span>
            <span>↗</span>
          </button>
        </div>

        <div className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      </div>
    </section>
  );
}
